import { DOCUMENT } from '@angular/common';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  ChallengeScreenError,
  ChallengeSuccessService
} from './challenge-success.service';

interface AkamaiChallengeEvent {
  isTrusted: boolean;
  url: string;
  type: string;
}

interface TrackJs {
  track(error: string): void;
  addMetadata(key: string, value: string): void;
  removeMetadata(key: string): void;
}

@Injectable()
export class HttpChallengeInterceptorService
  implements HttpInterceptor, OnDestroy
{
  private blockedURL = '';
  private readonly BLOCKED_ERROR_CODE = 428;
  private readonly ERROR_MESSAGE = 'User Blocked';

  // Event handlers
  private challengeStartedHandler!: (event: Event) => void;
  private challengeNoReloadHandler!: () => void;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private challengeSuccessService: ChallengeSuccessService
  ) {
    if (this.document) {
      // Define handlers
      this.challengeStartedHandler = this.handleChallengeStarted.bind(this);
      this.challengeNoReloadHandler = this.handleChallengeNoReload.bind(this);

      // Add event listeners
      this.document.addEventListener(
        'akamai-challenge-started',
        this.challengeStartedHandler
      );
      // Challenge Success and Modal closed
      this.document.addEventListener(
        'akamai-challenge-noreload',
        this.challengeNoReloadHandler
      );
    }
  }

  private handleChallengeStarted(event: Event): void {
    this.challengeSuccessService.resetValues();
    const akamaiEvent = event as unknown as AkamaiChallengeEvent;
    if (akamaiEvent.url) {
      this.blockedURL = akamaiEvent.url;
    }
  }

  private handleChallengeNoReload(): void {
    this.challengeSuccessService._callBackAfterSuccessChallenge$.next({
      url: this.blockedURL,
      callBack: true
    });
  }

  private isAkamaiChallenge(error: HttpErrorResponse): boolean {
    return Boolean(
      this.blockedURL &&
        error?.url === this.blockedURL &&
        error.status === this.BLOCKED_ERROR_CODE
    );
  }

  private createChallengeError(error: HttpErrorResponse) {
    this.trackError(this.blockedURL);
    return new ChallengeScreenError(
      this.ERROR_MESSAGE,
      this.blockedURL,
      'true',
      error?.error
    );
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.isAkamaiChallenge(error)) {
          return throwError(this.createChallengeError(error));
        }
        return throwError(error);
      })
    );
  }

  trackError(url: string): void {
    try {
      const trackJs = window?.trackJs as TrackJs | undefined;
      if (trackJs) {
        trackJs.addMetadata('blockedUrl', url);
        trackJs.track(
          `${this.ERROR_MESSAGE} Status: ${this.BLOCKED_ERROR_CODE}`
        );
        trackJs.removeMetadata('blockedUrl');
      }
    } catch (error) {
      console.error('Error tracking challenge:', error);
    }
  }

  ngOnDestroy(): void {
    if (this.document) {
      this.document.removeEventListener(
        'akamai-challenge-started',
        this.challengeStartedHandler
      );
      this.document.removeEventListener(
        'akamai-challenge-noreload',
        this.challengeNoReloadHandler
      );
    }
    this.challengeSuccessService._callBackAfterSuccessChallenge$.complete();
  }
}
